import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { resendVerificationEmail, verifyEmail } from '@/controller/auth';
import { toast } from '@/shared/components/UI';
import { validateAndSanitizeURL } from '@/shared/utils/sanitize';

const TokenExpired = () => {
    const [type, setType] = useState('---');
    const [load, setLoad] = useState(false);
    const [email, setEmail] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const nav = useNavigate();

    useEffect(() => {
        // expired || verified || resend
        const pType = searchParams.get('type');
        setType(pType || '---');
        if (pType === 'expired' || pType === 'resend') {
            const em = searchParams.get('email');
            if (em === null || em === '') {
                nav('/');
            } else {
                setEmail(em);
                searchParams.delete('type');
                searchParams.delete('email');
                setSearchParams(searchParams);
            }
        } else if (pType === 'verify') {
            const tenantId = searchParams.get('tenant');
            const email = searchParams.get('email');
            const token = searchParams.get('token');
            console.log(tenantId, email, token);

            const fetchData = async () => {
                try {
                    const ret = await verifyEmail(tenantId, email, token);

                    if (ret.data.success === true) {
                        setType('verified');
                    } else if (ret.data?.error?.error_code === 100001) {
                        // Expired token
                        setEmail(email);
                        setType('expired'); // Let the existing UI handle it
                    } else {
                        toast.error(ret.data?.error?.title || 'Email verification failed');
                        nav('/');
                    }
                } catch (error) {
                    toast.error('An error occurred during verification');
                    nav('/');
                }
            };

            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resend = async () => {
        setLoad(true);
        try {
            const ret = await resendVerificationEmail(email);
            if (ret.status === 200) {
                toast.success('Sent successfully. Please proceed to your email to verify account.');
                nav('/verification?email=' + email + '&type=signup-success');
                setLoad(false);
            } else {
                toast.error(ret.data.message);
                setLoad(false);
            }
        } catch (e) {}
    };

    if (type === 'expired' || type === 'resend') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Your email verification link has expired
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    Looks like the verification link sent to{' '}
                                    <span className="text-[#111218] font-medium">{email}</span> has expired.
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="px-4 py-6 text-center">
                            <button
                                className={`min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full`}
                                disabled={load}
                                onClick={resend}
                            >
                                {load ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Sending...</span>
                                    </div>
                                ) : (
                                    'Resend Verification Email'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (type === 'verify') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                <div className="flex justify-center mb-6">
                                    <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                                </div>

                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Checking...
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    Please wait while we verify your account.
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="p-4 flex justify-center">
                            <button
                                className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                                onClick={() => nav('/')}
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (type === 'verified') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Your account is verified.
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    Great! Your email has been verified and your account is ready to use.
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="p-4 flex justify-center">
                            <button
                                className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                                onClick={() => nav('/')}
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        if (type !== '---') {
            return <Navigate to="/" />;
        }
    }
};

export default TokenExpired;
